package top.wcpe.allincore.dsl.compiler.parse

import top.wcpe.allincore.dsl.compiler.ast.ExprAst
import top.wcpe.allincore.dsl.compiler.ast.SubscribeBlockAst
import top.wcpe.allincore.dsl.compiler.lex.TokenType

/**
 * 解析 screen 级 `subscribe { ns { topic { handler = expr } } }` 块。
 *
 * 结构：
 * ```
 * subscribe {
 *     shop {
 *         priceFeed { handler = ::onPriceFeed }
 *         stock { handler = { event -> state.x = event.value } }
 *     }
 *     chat {
 *         broadcast { handler = ::onBroadcast }
 *     }
 * }
 * ```
 *
 * 错误码分工：parser 仅做结构性解析与"必须是标识符"这种语法层校验；
 * 命名合法性（E781/E782）、handler 形态（E784）、未知字段（E785）等语义级
 * 错误由 [top.wcpe.allincore.dsl.compiler.semantic.SubscribeChecker] 统一报，
 * 以便诊断聚合（一次编译尽可能多报错误）。
 */
internal class SubscribeBlockParser(private val p: Parser) {

    fun parse(): SubscribeBlockAst {
        val start = p.current()
        p.advance() // 消费 subscribe 标识符
        p.skipNewlines()
        p.expect(TokenType.LBRACE, "subscribe 后缺少 '{'")
        skipSeparators()

        val items = mutableListOf<SubscribeBlockAst.Item>()
        val unknownFields = mutableListOf<SubscribeBlockAst.UnknownField>()
        while (!p.check(TokenType.RBRACE) && !p.check(TokenType.EOF)) {
            parseNamespaceBlock(items, unknownFields)
            if (p.check(TokenType.RBRACE) || p.check(TokenType.EOF)) break
            skipSeparators()
        }
        val close = p.expect(TokenType.RBRACE, "subscribe 块缺少 '}'")
        return SubscribeBlockAst(items, unknownFields, p.completePosition(start.position, close))
    }

    /** 解析 `namespace { topic { ... } ... }` 层。 */
    private fun parseNamespaceBlock(
        items: MutableList<SubscribeBlockAst.Item>,
        unknownFields: MutableList<SubscribeBlockAst.UnknownField>,
    ) {
        // namespace 名仅要求是 IDENTIFIER token；命名合法性由 SubscribeChecker 报 E781。
        val nsTok = p.expect(TokenType.IDENTIFIER, "subscribe 命名空间必须是标识符")
        p.skipNewlines()
        p.expect(TokenType.LBRACE, "subscribe 命名空间 '${nsTok.lexeme}' 后缺少 '{'")
        skipSeparators()

        while (!p.check(TokenType.RBRACE) && !p.check(TokenType.EOF)) {
            parseTopicBlock(nsTok.lexeme, items, unknownFields)
            if (p.check(TokenType.RBRACE) || p.check(TokenType.EOF)) break
            skipSeparators()
        }
        p.expect(TokenType.RBRACE, "subscribe 命名空间 '${nsTok.lexeme}' 块缺少 '}'")
        skipSeparators()
    }

    /** 解析 `topic { handler = expr }` 层。 */
    private fun parseTopicBlock(
        namespace: String,
        items: MutableList<SubscribeBlockAst.Item>,
        unknownFields: MutableList<SubscribeBlockAst.UnknownField>,
    ) {
        // topic 名仅要求是 IDENTIFIER token；命名合法性由 SubscribeChecker 报 E782。
        val topicTok = p.expect(TokenType.IDENTIFIER, "subscribe 主题名必须是标识符")
        p.skipNewlines()
        p.expect(TokenType.LBRACE, "subscribe 主题 '${topicTok.lexeme}' 后缺少 '{'")
        skipSeparators()

        var handler: ExprAst? = null
        while (!p.check(TokenType.RBRACE) && !p.check(TokenType.EOF)) {
            val fieldTok = p.expect(TokenType.IDENTIFIER, "subscribe 条目字段名必须是标识符")
            p.skipNewlines()
            p.expect(TokenType.EQUALS, "subscribe 字段 '${fieldTok.lexeme}' 缺少 '='")
            p.skipNewlines()
            val value = ActionBlockParser(p).expressionParser(allowBareIdent = false).parse()
            if (fieldTok.lexeme == "handler") {
                handler = value
            } else {
                // 未知字段保留到 AST，由 SubscribeChecker 统一报 E785。
                unknownFields += SubscribeBlockAst.UnknownField(
                    namespace = namespace,
                    topic = topicTok.lexeme,
                    fieldName = fieldTok.lexeme,
                    position = fieldTok.position,
                )
            }
            if (p.check(TokenType.RBRACE) || p.check(TokenType.EOF)) break
            expectSeparator("subscribe 字段后缺少换行或 ';'")
            skipSeparators()
        }
        p.expect(TokenType.RBRACE, "subscribe 主题 '${topicTok.lexeme}' 块缺少 '}'")
        skipSeparators()

        if (handler == null) {
            p.fail("subscribe 主题 '${topicTok.lexeme}' 缺少 handler 字段", topicTok.position)
        }
        items += SubscribeBlockAst.Item(namespace, topicTok.lexeme, handler, topicTok.position)
    }

    private fun skipSeparators() {
        while (p.check(TokenType.NEWLINE) || p.check(TokenType.SEMICOLON)) p.advance()
    }

    private fun expectSeparator(message: String) {
        if (!p.check(TokenType.NEWLINE) && !p.check(TokenType.SEMICOLON)) {
            p.fail(message, p.current().position)
        }
    }
}
